import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { database } from '../../firebase';
import { ref, get, update } from 'firebase/database';
import Swal from 'sweetalert2';
import {
  ShoppingBagIcon, ClockIcon, CheckCircleIcon, XCircleIcon,
  ArrowPathIcon, ChevronRightIcon, ChevronLeftIcon,
  TagIcon, CreditCardIcon, XMarkIcon, MapPinIcon,
  PhoneIcon, EnvelopeIcon, CalendarIcon, SparklesIcon,
  ExclamationTriangleIcon, InformationCircleIcon,
  EyeIcon, PencilIcon, BuildingOfficeIcon,
} from '@heroicons/react/24/outline';
import {
  CheckCircleIcon as CheckCircleSolid,
  ShoppingBagIcon as BagSolid,
} from '@heroicons/react/24/solid';

if (typeof document !== 'undefined' && !document.getElementById('swal-orders-z')) {
  const s = document.createElement('style');
  s.id = 'swal-orders-z';
  s.textContent = `.swal2-container{z-index:999999!important}.swal2-on-top{z-index:999999!important}`;
  document.head.appendChild(s);
}
const toast = (icon, title, text = '') =>
  Swal.fire({ icon, title, text, timer: 2800, toast: true, position: 'top-end', showConfirmButton: false, customClass: { container: 'swal2-on-top' } });

const WhatsAppIcon = ({ className = 'h-4 w-4' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const ORDER_STATUS = {
  pending:   { label: 'Pending',    color: '#f59e0b', bg: '#fffbeb', border: '#fde68a', icon: <ClockIcon        className="h-4 w-4" /> },
  processed: { label: 'Processing', color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe', icon: <ArrowPathIcon    className="h-4 w-4" /> },
  ready:     { label: 'Ready',      color: '#8b5cf6', bg: '#f5f3ff', border: '#ddd6fe', icon: <CheckCircleIcon  className="h-4 w-4" /> },
  collected: { label: 'Collected',  color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0', icon: <CheckCircleSolid className="h-4 w-4" /> },
  cancelled: { label: 'Cancelled',  color: '#ef4444', bg: '#fef2f2', border: '#fecaca', icon: <XCircleIcon      className="h-4 w-4" /> },
};

const PAYMENT_STATUS = {
  pending:  { label: 'Unpaid',   color: '#f59e0b', bg: '#fffbeb', border: '#fde68a' },
  paid:     { label: 'Paid',     color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
  failed:   { label: 'Failed',   color: '#ef4444', bg: '#fef2f2', border: '#fecaca' },
  refunded: { label: 'Refunded', color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb' },
};

const PAYMENT_METHOD = {
  whatsapp_cod: { label: 'WhatsApp / COD',  icon: <WhatsAppIcon className="h-3.5 w-3.5" />,   color: '#25D366' },
  paystack:     { label: 'Paystack',        icon: <CreditCardIcon className="h-3.5 w-3.5" />, color: '#800020' },
  mpesa:        { label: 'M-Pesa',          icon: <PhoneIcon className="h-3.5 w-3.5" />,      color: '#16a34a' },
  visa:         { label: 'Visa/Mastercard', icon: <CreditCardIcon className="h-3.5 w-3.5" />, color: '#1d4ed8' },
  wallet:       { label: 'Wallet',          icon: <SparklesIcon className="h-3.5 w-3.5" />,   color: '#7c3aed' },
  cash:         { label: 'Cash',            icon: <TagIcon className="h-3.5 w-3.5" />,         color: '#374151' },
};

const WHATSAPP_NUMBER = '254748798287';

const fmt = (ts) => {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};
const fmtDate = (ts) => {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' });
};

const canPay = (order) =>
  order.paymentStatus !== 'paid' &&
  order.paymentStatus !== 'refunded' &&
  order.orderStatus   !== 'cancelled' &&
  order.orderStatus   !== 'collected';

const StatusBadge = ({ type, value, small = false }) => {
  const cfg = type === 'order' ? ORDER_STATUS[value] : PAYMENT_STATUS[value];
  if (!cfg) return null;
  return (
    <span className={`inline-flex items-center gap-1 font-semibold rounded-full border ${small ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2.5 py-1'}`}
      style={{ color: cfg.color, background: cfg.bg, borderColor: cfg.border || cfg.bg }}>
      {!small && cfg.icon}
      {cfg.label}
    </span>
  );
};

const showPayNowSwal = async (order) => {
  await Swal.fire({
    title: '💳 Online Payment',
    html: `
      <div style="text-align:center;padding:8px 0 4px">
        <div style="font-size:52px;margin-bottom:12px">🚧</div>
        <p style="color:#374151;font-size:14px;line-height:1.6;margin-bottom:10px">
          Online payment via <strong>Paystack</strong> is being integrated and will be available very soon.
        </p>
        <div style="background:#fffbeb;border:1px solid #fde68a;border-radius:12px;padding:12px;text-align:left;margin-top:8px">
          <p style="font-size:12px;color:#92400e;font-weight:600;margin-bottom:4px">In the meantime:</p>
          <p style="font-size:12px;color:#92400e;line-height:1.5">
            Reply to your order message and arrange M-Pesa or cash payment directly with us.<br/>
            Order: <strong>${order.orderNumber}</strong> · Total: <strong>KES ${order.total?.toLocaleString()}</strong>
          </p>
        </div>
      </div>
    `,
    icon: undefined,
    confirmButtonText: 'Got it',
    confirmButtonColor: '#800020',
    customClass: { container: 'swal2-on-top', popup: 'rounded-2xl' },
  });
};

// ─── VIEW ITEMS MODAL ─────────────────────────────────────────────────────────
const ViewItemsModal = ({ order, onClose }) => {
  if (!order) return null;
  const orderSt  = ORDER_STATUS[order.orderStatus]    || ORDER_STATUS.pending;
  const paymentSt = PAYMENT_STATUS[order.paymentStatus] || PAYMENT_STATUS.pending;
  const payMethod = PAYMENT_METHOD[order.paymentMethod] || { label: order.paymentMethod || '—', icon: null, color: '#374151' };
  const isWhatsApp = order.paymentMethod === 'whatsapp_cod';

  const reopenWhatsApp = () => {
    const items = (order.items || []).map(i =>
      `  • ${i.name} x${i.quantity} — KES ${(i.price * i.quantity)?.toLocaleString()}`
    ).join('%0A');
    const msg =
      `📋 *Order Follow-up: ${order.orderNumber}*%0A` +
      `══════════════════%0A` +
      `Items:%0A${items}%0A%0A` +
      `💰 Total: KES ${order.total?.toLocaleString()}%0A` +
      `Status: ${orderSt.label} · Payment: ${paymentSt.label}%0A%0A` +
      `I'd like to follow up on this order. Thank you!`;
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${msg}`, '_blank');
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0"
      style={{ zIndex: 99999, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}>
      <motion.div
        initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 280 }}
        className="flex flex-col bg-white shadow-2xl"
        style={{ position:"fixed", inset:0, zIndex: 100000 }}
        onClick={e => e.stopPropagation()}>

        <div className="flex-shrink-0 flex items-center justify-between px-6 py-4"
          style={{ background: 'linear-gradient(135deg,#1a1a2e 0%,#800020 100%)' }}>
          <div>
            <h2 className="text-base font-extrabold text-white">{order.orderNumber}</h2>
            <p className="text-white/55 text-xs mt-0.5">{fmtDate(order.createdAt)}</p>
          </div>
          <button onClick={onClose}
            className="w-9 h-9 rounded-xl flex items-center justify-center text-white transition-all"
            style={{ background: 'rgba(255,255,255,0.12)' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.22)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}>
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">

            {/* Status row */}
            <div className="flex flex-wrap gap-3">
              <div className="flex flex-col gap-1">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Order Status</span>
                <StatusBadge type="order" value={order.orderStatus} />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Payment</span>
                <StatusBadge type="payment" value={order.paymentStatus} />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Method</span>
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border border-gray-200"
                  style={{ color: payMethod.color }}>
                  {payMethod.icon}{payMethod.label}
                </span>
              </div>
            </div>

            {/* Items */}
            <div>
              <p className="text-sm font-extrabold text-gray-900 mb-3">Order Items ({order.items?.length || 0})</p>
              <div className="space-y-2.5">
                {(order.items || []).map((item, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <div className="w-14 h-14 rounded-xl bg-white border border-gray-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {item.image
                        ? <img src={item.image} alt={item.name} className="w-full h-full object-contain p-1" />
                        : <TagIcon className="h-6 w-6 text-gray-300" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800">{item.name}</p>
                      <p className="text-xs text-gray-400 mt-0.5">Qty: {item.quantity} × KES {item.price?.toLocaleString()}</p>
                      {(item.variant?.color || item.variant?.size) && (
                        <p className="text-[10px] text-gray-400 mt-0.5">
                          {item.variant.color && `Color: ${item.variant.color}`}
                          {item.variant.color && item.variant.size && ' · '}
                          {item.variant.size && `Size: ${item.variant.size}`}
                        </p>
                      )}
                    </div>
                    <p className="text-sm font-bold flex-shrink-0" style={{ color: '#800020' }}>
                      KES {(item.price * item.quantity)?.toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Totals */}
            <div className="bg-gray-50 rounded-2xl p-4 space-y-2">
              {order.savings > 0 && (
                <div className="flex justify-between text-xs">
                  <span className="text-green-600 font-semibold">Savings</span>
                  <span className="font-bold text-green-600">- KES {order.savings?.toLocaleString()}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Subtotal</span>
                <span className="font-semibold">KES {order.subtotal?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                <span className="text-base font-extrabold text-gray-900">Total</span>
                <span className="text-xl font-extrabold" style={{ color: '#800020' }}>KES {order.total?.toLocaleString()}</span>
              </div>
            </div>

            {/* Timeline */}
            <div>
              <p className="text-sm font-extrabold text-gray-900 mb-3">Timeline</p>
              <div className="space-y-2">
                {[
                  { label: 'Order Placed',     ts: order.createdAt,   show: true },
                  { label: 'Processed',        ts: order.processedAt, show: !!order.processedAt },
                  { label: 'Ready for Pickup', ts: order.readyAt,     show: !!order.readyAt },
                  { label: 'Completed',        ts: order.collectedAt, show: !!order.collectedAt },
                ].filter(t => t.show).map((t, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: '#800020' }} />
                    <span className="text-xs text-gray-500 flex-1">{t.label}</span>
                    <span className="text-xs text-gray-400">{fmt(t.ts)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100 bg-white flex flex-col sm:flex-row gap-3">
          {canPay(order) && (
            <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
              onClick={() => showPayNowSwal(order)}
              className="flex-1 py-3 rounded-2xl text-sm font-bold text-white flex items-center justify-center gap-2 shadow"
              style={{ background: 'linear-gradient(135deg,#800020,#b30000)' }}>
              <CreditCardIcon className="h-4 w-4" />Pay Now
            </motion.button>
          )}
          {isWhatsApp && (
            <button onClick={reopenWhatsApp}
              className="flex-1 py-3 rounded-2xl text-sm font-bold text-white flex items-center justify-center gap-2 shadow"
              style={{ background: '#25D366' }}
              onMouseEnter={e => e.currentTarget.style.background = '#1da851'}
              onMouseLeave={e => e.currentTarget.style.background = '#25D366'}>
              <WhatsAppIcon className="h-4 w-4" />Message Us
            </button>
          )}
          <button onClick={onClose}
            className="flex-1 sm:flex-none px-6 py-3 rounded-2xl text-sm font-semibold border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all">
            Close
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ─── EDIT DELIVERY MODAL ──────────────────────────────────────────────────────
const EditDeliveryModal = ({ order, onClose, onSaved }) => {
  const [saving,    setSaving]    = useState(false);
  const [activeTab, setActiveTab] = useState('contact');

  const [contact, setContact] = useState({
    name:    order?.userInfo?.name    || '',
    email:   order?.userInfo?.email   || '',
    phone:   order?.userInfo?.phone   || '',
    company: order?.userInfo?.company || '',
  });

  const [delivery, setDelivery] = useState({
    address:     order?.deliveryInfo?.address     || '',
    area:        order?.deliveryInfo?.area        || '',
    city:        order?.deliveryInfo?.city        || '',
    landmark:    order?.deliveryInfo?.landmark    || '',
    notes:       order?.deliveryInfo?.notes       || '',
    contactPref: order?.deliveryInfo?.contactPref || 'phone',
  });

  if (!order) return null;

  const save = async () => {
    setSaving(true);
    try {
      await update(ref(database, `orders/${order.id}`), {
        userInfo:     { ...contact },
        deliveryInfo: { ...delivery },
      });
      toast('success', 'Details updated successfully');
      onSaved({ ...order, userInfo: { ...contact }, deliveryInfo: { ...delivery } });
      onClose();
    } catch (e) {
      console.error(e);
      toast('error', 'Could not save', 'Please try again');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:outline-none transition-all';
  const focusCls = { borderColor: '#800020' };

  const tabs = [
    { key: 'contact',  label: 'Contact Info' },
    { key: 'delivery', label: 'Delivery Info' },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0"
      style={{ zIndex: 99999, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}>
      <motion.div
        initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 280 }}
        className="flex flex-col bg-white shadow-2xl"
        style={{ position:"fixed", inset:0, zIndex: 100000 }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between px-6 py-4"
          style={{ background: 'linear-gradient(135deg,#1a1a2e 0%,#800020 100%)' }}>
          <div>
            <h2 className="text-base font-extrabold text-white">Edit Order Details</h2>
            <p className="text-white/55 text-xs mt-0.5">{order.orderNumber}</p>
          </div>
          <button onClick={onClose}
            className="w-9 h-9 rounded-xl flex items-center justify-center text-white"
            style={{ background: 'rgba(255,255,255,0.12)' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.22)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}>
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex-shrink-0 flex border-b border-gray-100 px-6 pt-4 gap-4">
          {tabs.map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              className="pb-3 text-sm font-semibold border-b-2 transition-all"
              style={{
                borderColor: activeTab === t.key ? '#800020' : 'transparent',
                color:       activeTab === t.key ? '#800020' : '#9ca3af',
              }}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          <AnimatePresence mode="wait">
            {activeTab === 'contact' && (
              <motion.div key="contact" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}
                className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Full Name</label>
                    <input value={contact.name} onChange={e => setContact(c => ({ ...c, name: e.target.value }))}
                      placeholder="John Doe" className={inputCls}
                      onFocus={e => e.target.style.borderColor = '#800020'}
                      onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Company</label>
                    <input value={contact.company} onChange={e => setContact(c => ({ ...c, company: e.target.value }))}
                      placeholder="Optional" className={inputCls}
                      onFocus={e => e.target.style.borderColor = '#800020'}
                      onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Email</label>
                  <div className="relative">
                    <EnvelopeIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input value={contact.email} onChange={e => setContact(c => ({ ...c, email: e.target.value }))}
                      placeholder="john@example.com" type="email"
                      className={`${inputCls} pl-10`}
                      onFocus={e => e.target.style.borderColor = '#800020'}
                      onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Phone</label>
                  <div className="relative">
                    <PhoneIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input value={contact.phone} onChange={e => setContact(c => ({ ...c, phone: e.target.value }))}
                      placeholder="0712 345 678" type="tel"
                      className={`${inputCls} pl-10`}
                      onFocus={e => e.target.style.borderColor = '#800020'}
                      onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
                  </div>
                </div>

                {/* Preferred contact */}
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Preferred Contact</label>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { val: 'phone', label: 'Phone / WhatsApp' },
                      { val: 'email', label: 'Email' },
                    ].map(({ val, label }) => (
                      <button key={val} type="button"
                        onClick={() => setDelivery(d => ({ ...d, contactPref: val }))}
                        className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 text-sm font-semibold transition-all"
                        style={{
                          borderColor: delivery.contactPref === val ? '#800020' : '#e5e7eb',
                          background:  delivery.contactPref === val ? '#fef2f2' : '#fff',
                          color:       delivery.contactPref === val ? '#800020' : '#6b7280',
                        }}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {activeTab === 'delivery' && (
              <motion.div key="delivery" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}
                className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Street Address</label>
                  <div className="relative">
                    <MapPinIcon className="absolute left-3.5 top-3.5 h-4 w-4 text-gray-400" />
                    <input value={delivery.address} onChange={e => setDelivery(d => ({ ...d, address: e.target.value }))}
                      placeholder="e.g. 24 Ngong Road, Apt 5B"
                      className={`${inputCls} pl-10`}
                      onFocus={e => e.target.style.borderColor = '#800020'}
                      onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Area / Estate</label>
                    <input value={delivery.area} onChange={e => setDelivery(d => ({ ...d, area: e.target.value }))}
                      placeholder="e.g. Westlands" className={inputCls}
                      onFocus={e => e.target.style.borderColor = '#800020'}
                      onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">City / Town</label>
                    <input value={delivery.city} onChange={e => setDelivery(d => ({ ...d, city: e.target.value }))}
                      placeholder="Nairobi" className={inputCls}
                      onFocus={e => e.target.style.borderColor = '#800020'}
                      onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Landmark</label>
                  <input value={delivery.landmark} onChange={e => setDelivery(d => ({ ...d, landmark: e.target.value }))}
                    placeholder="e.g. Near Sarit Centre" className={inputCls}
                    onFocus={e => e.target.style.borderColor = '#800020'}
                    onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Delivery Notes</label>
                  <textarea value={delivery.notes} onChange={e => setDelivery(d => ({ ...d, notes: e.target.value }))}
                    rows={3} placeholder="Any special instructions…"
                    className={`${inputCls} resize-none`}
                    onFocus={e => e.target.style.borderColor = '#800020'}
                    onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100 bg-white flex gap-3">
          <button onClick={onClose}
            className="flex-1 py-3 rounded-2xl text-sm font-semibold border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all">
            Cancel
          </button>
          <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
            onClick={save} disabled={saving}
            className="flex-1 py-3 rounded-2xl text-sm font-bold text-white shadow transition-all disabled:opacity-60"
            style={{ background: 'linear-gradient(135deg,#800020,#b30000)' }}>
            {saving ? 'Saving…' : 'Save Changes'}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ─── ORDER CARD ───────────────────────────────────────────────────────────────
const OrderCard = ({ order, onViewItems, onEditDelivery }) => {
  const payMethod  = PAYMENT_METHOD[order.paymentMethod] || { label: order.paymentMethod || '—', icon: null, color: '#374151' };
  const isWhatsApp = order.paymentMethod === 'whatsapp_cod';
  const totalItems = (order.items || []).reduce((s, i) => s + (i.quantity || 1), 0);
  const orderSt    = ORDER_STATUS[order.orderStatus] || ORDER_STATUS.pending;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">

      {/* Card header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50"
        style={{ background: 'linear-gradient(135deg,#f8f9fa,#fef2f2)' }}>
        <div>
          <p className="text-sm font-extrabold text-gray-900">{order.orderNumber}</p>
          <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
            <CalendarIcon className="h-3 w-3" />{fmtDate(order.createdAt)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="hidden sm:inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border border-gray-200"
            style={{ color: payMethod.color }}>
            {payMethod.icon}{isWhatsApp ? 'WhatsApp' : payMethod.label}
          </span>
        </div>
      </div>

      {/* Status + total */}
      <div className="px-5 py-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge type="order"   value={order.orderStatus}   small />
            <StatusBadge type="payment" value={order.paymentStatus} small />
          </div>
          <p className="text-base font-extrabold" style={{ color: '#800020' }}>
            KES {order.total?.toLocaleString()}
          </p>
        </div>

        {/* Items count */}
        <p className="text-xs text-gray-500 mb-3">
          {totalItems} item{totalItems !== 1 ? 's' : ''} · {(order.items || []).length} product{(order.items || []).length !== 1 ? 's' : ''}
        </p>

        {/* Action buttons */}
        <div className="grid grid-cols-3 gap-2">
          {/* View Items */}
          <button onClick={() => onViewItems(order)}
            className="flex flex-col items-center justify-center gap-1 py-2.5 rounded-xl text-xs font-semibold transition-all"
            style={{ background: '#f0f9ff', color: '#0369a1' }}
            onMouseEnter={e => e.currentTarget.style.background = '#e0f2fe'}
            onMouseLeave={e => e.currentTarget.style.background = '#f0f9ff'}>
            <EyeIcon className="h-4 w-4" />
            View Items
          </button>

          {/* Pay Now */}
          <button
            onClick={() => canPay(order) ? showPayNowSwal(order) : null}
            disabled={!canPay(order)}
            className="flex flex-col items-center justify-center gap-1 py-2.5 rounded-xl text-xs font-semibold transition-all"
            style={{
              background: canPay(order) ? '#fef2f2' : '#f9fafb',
              color:      canPay(order) ? '#800020' : '#9ca3af',
              cursor:     canPay(order) ? 'pointer' : 'not-allowed',
            }}
            onMouseEnter={e => { if (canPay(order)) e.currentTarget.style.background = '#fee2e2'; }}
            onMouseLeave={e => { if (canPay(order)) e.currentTarget.style.background = '#fef2f2'; }}>
            <CreditCardIcon className="h-4 w-4" />
            Pay Now
          </button>

          {/* Edit Delivery */}
          <button
            onClick={() => onEditDelivery(order)}
            disabled={order.orderStatus === 'collected' || order.orderStatus === 'cancelled'}
            className="flex flex-col items-center justify-center gap-1 py-2.5 rounded-xl text-xs font-semibold transition-all"
            style={{
              background: (order.orderStatus === 'collected' || order.orderStatus === 'cancelled') ? '#f9fafb' : '#f0fdf4',
              color:      (order.orderStatus === 'collected' || order.orderStatus === 'cancelled') ? '#9ca3af' : '#16a34a',
              cursor:     (order.orderStatus === 'collected' || order.orderStatus === 'cancelled') ? 'not-allowed' : 'pointer',
            }}
            onMouseEnter={e => { if (order.orderStatus !== 'collected' && order.orderStatus !== 'cancelled') e.currentTarget.style.background = '#dcfce7'; }}
            onMouseLeave={e => { if (order.orderStatus !== 'collected' && order.orderStatus !== 'cancelled') e.currentTarget.style.background = '#f0fdf4'; }}>
            <PencilIcon className="h-4 w-4" />
            Edit Details
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// ─── FILTER TABS ─────────────────────────────────────────────────────────────
const FilterTabs = ({ active, onChange, counts }) => {
  const tabs = [
    { key: 'all',       label: 'All Orders' },
    { key: 'pending',   label: 'Pending'    },
    { key: 'active',    label: 'Active'     },
    { key: 'completed', label: 'Completed'  },
    { key: 'cancelled', label: 'Cancelled'  },
  ];
  return (
    <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none">
      {tabs.map(tab => (
        <button key={tab.key} onClick={() => onChange(tab.key)}
          className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-all"
          style={{
            background: active === tab.key ? '#800020' : '#fff',
            color:      active === tab.key ? '#fff'    : '#6b7280',
            border:     active === tab.key ? '2px solid #800020' : '2px solid #e5e7eb',
          }}>
          {tab.label}
          {counts[tab.key] > 0 && (
            <span className="text-[10px] font-extrabold rounded-full px-1.5 py-0.5 min-w-[18px] text-center"
              style={{
                background: active === tab.key ? 'rgba(255,255,255,0.25)' : '#f3f4f6',
                color:      active === tab.key ? '#fff' : '#6b7280',
              }}>
              {counts[tab.key]}
            </span>
          )}
        </button>
      ))}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN ORDERS PAGE
// ═══════════════════════════════════════════════════════════════════════════════
const Orders = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [orders,        setOrders]        = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [activeFilter,  setActiveFilter]  = useState('all');
  const [searchQuery,   setSearchQuery]   = useState('');
  const [viewItemsOrder,   setViewItemsOrder]   = useState(null);
  const [editDeliveryOrder, setEditDeliveryOrder] = useState(null);

  const loadOrders = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    try {
      const recentRef  = ref(database, `users/${currentUser.uid}/recentOrders`);
      const recentSnap = await get(recentRef);

      if (!recentSnap.exists()) { setOrders([]); setLoading(false); return; }

      const orderIds = Object.values(recentSnap.val()).map(r => r.orderId).filter(Boolean);
      if (!orderIds.length) { setOrders([]); setLoading(false); return; }

      const results = (await Promise.all(
        orderIds.map(async (orderId) => {
          try {
            const snap = await get(ref(database, `orders/${orderId}`));
            if (!snap.exists()) return null;
            const data = snap.val();
            if (!data.userId || data.userId === 'guest' || data.userId !== currentUser.uid) return null;
            return { id: orderId, ...data };
          } catch { return null; }
        })
      )).filter(Boolean).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

      setOrders(results);
    } catch (e) {
      console.error('Orders load error:', e);
      toast('error', 'Could not load orders', 'Please try again');
    } finally { setLoading(false); }
  }, [currentUser]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  const filterOrder = (o) => {
    switch (activeFilter) {
      case 'pending':   return o.orderStatus === 'pending';
      case 'active':    return ['processed', 'ready'].includes(o.orderStatus);
      case 'completed': return o.orderStatus === 'collected';
      case 'cancelled': return o.orderStatus === 'cancelled';
      default:          return true;
    }
  };

  const searchMatch = (o) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      o.orderNumber?.toLowerCase().includes(q) ||
      (o.items || []).some(i => i.name?.toLowerCase().includes(q))
    );
  };

  const filtered = orders.filter(o => filterOrder(o) && searchMatch(o));

  const counts = {
    all:       orders.length,
    pending:   orders.filter(o => o.orderStatus === 'pending').length,
    active:    orders.filter(o => ['processed','ready'].includes(o.orderStatus)).length,
    completed: orders.filter(o => o.orderStatus === 'collected').length,
    cancelled: orders.filter(o => o.orderStatus === 'cancelled').length,
  };

  const handleDeliverySaved = (updatedOrder) => {
    setOrders(prev => prev.map(o => o.id === updatedOrder.id ? updatedOrder : o));
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-4" style={{ background: '#f8f9fa' }}>
        <BagSolid className="h-16 w-16 text-gray-200" />
        <h2 className="text-xl font-bold text-gray-700">Sign in to view your orders</h2>
        <button onClick={() => navigate('/login')}
          className="px-6 py-3 rounded-2xl text-white font-bold text-sm"
          style={{ background: 'linear-gradient(135deg,#800020,#b30000)' }}>
          Sign In
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#f8f9fa' }}>
        <div className="flex flex-col items-center gap-3">
          <ArrowPathIcon className="h-10 w-10 animate-spin" style={{ color: '#800020' }} />
          <p className="text-sm text-gray-400 font-medium">Loading your orders…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg,#f8f9fa 0%,#f1f3f5 100%)' }}>

      <div className="bg-white border-b border-gray-100 sticky top-0" style={{ zIndex: 40 }}>
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)}
              className="w-9 h-9 flex items-center justify-center rounded-xl border-2 border-gray-200 text-gray-500 hover:border-gray-300 transition-all">
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <div className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg,#800020,#b30000)' }}>
              <ShoppingBagIcon className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-extrabold text-gray-900">My Orders</h1>
              <p className="text-xs text-gray-400">
                {orders.length === 0 ? 'No orders yet' : `${orders.length} order${orders.length !== 1 ? 's' : ''}`}
              </p>
            </div>
          </div>
          <button onClick={loadOrders}
            className="p-2 rounded-xl text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-all" title="Refresh">
            <ArrowPathIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-6">

        {orders.length === 0 ? (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center py-24 text-center bg-white rounded-2xl border border-gray-100 shadow-sm">
            <div className="w-28 h-28 rounded-full flex items-center justify-center mb-6"
              style={{ background: 'linear-gradient(135deg,#fef2f2,#fee2e2)' }}>
              <ShoppingBagIcon className="h-14 w-14" style={{ color: '#fca5a5' }} />
            </div>
            <h2 className="text-2xl font-extrabold text-gray-900 mb-2">No orders yet</h2>
            <p className="text-gray-500 text-sm mb-8 max-w-xs leading-relaxed">
              When you place an order it will appear here. Start browsing our products!
            </p>
            <motion.button whileHover={{ scale: 1.04, boxShadow: '0 8px 28px rgba(128,0,32,0.35)' }} whileTap={{ scale: 0.97 }}
              onClick={() => navigate('/shop')}
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-2xl text-sm font-bold text-white shadow-lg"
              style={{ background: 'linear-gradient(135deg,#800020,#b30000)' }}>
              <SparklesIcon className="h-4 w-4" />Shop Now
            </motion.button>
          </motion.div>
        ) : (
          <>
            <div className="relative mb-4">
              <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search by order number or product name…"
                className="w-full pl-10 pr-10 py-3 bg-white border-2 border-gray-200 rounded-xl text-sm focus:outline-none transition-all shadow-sm"
                style={{ borderColor: searchQuery ? '#800020' : '#e5e7eb' }} />
              <TagIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <XMarkIcon className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="mb-6">
              <FilterTabs active={activeFilter} onChange={setActiveFilter} counts={counts} />
            </div>

            {orders.some(canPay) && (
              <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
                className="flex items-start gap-3 p-4 rounded-2xl border mb-5"
                style={{ background: '#fffbeb', borderColor: '#fde68a' }}>
                <InformationCircleIcon className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700 leading-relaxed">
                  <strong>Paystack online payment is coming soon.</strong> Use <em>View Items → Message Us</em> to arrange payment via WhatsApp.
                </p>
              </motion.div>
            )}

            {filtered.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
                <p className="text-gray-400 font-medium">No orders match your filters</p>
                <button onClick={() => { setActiveFilter('all'); setSearchQuery(''); }}
                  className="mt-2 text-sm font-semibold" style={{ color: '#800020' }}>
                  Clear filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <AnimatePresence>
                  {filtered.map(order => (
                    <OrderCard
                      key={order.id}
                      order={order}
                      onViewItems={setViewItemsOrder}
                      onEditDelivery={setEditDeliveryOrder}
                    />
                  ))}
                </AnimatePresence>
              </div>
            )}

            {filtered.length > 0 && (
              <p className="text-xs text-gray-400 text-center mt-6">
                Showing {filtered.length} of {orders.length} order{orders.length !== 1 ? 's' : ''}
              </p>
            )}
          </>
        )}
      </div>

      <AnimatePresence>
        {viewItemsOrder && (
          <ViewItemsModal order={viewItemsOrder} onClose={() => setViewItemsOrder(null)} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {editDeliveryOrder && (
          <EditDeliveryModal
            order={editDeliveryOrder}
            onClose={() => setEditDeliveryOrder(null)}
            onSaved={handleDeliverySaved}
          />
        )}
      </AnimatePresence>

      <style>{`
        .scrollbar-none::-webkit-scrollbar{display:none;}
        .scrollbar-none{-ms-overflow-style:none;scrollbar-width:none;}
      `}</style>
    </div>
  );
};

export default Orders;